import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Lock, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/api";
import { isAstriaDomain } from "../../utils/branding";
import {
  WorldRegion,
  getRegionsByContinent,
  formatRegionPrice,
  isSameRegion,
} from "../../utils/regions";

const COLORS = {
  amethyst: "#6D4C92",
  darkAmethyst: "#2D1B44",
  roseGold: "#E5B8AD",
  ink: "#4A3F35",
  muted: "#8A7F73",
  hairline: "#EDE8E0",
  cream: "#F6EBDD",
};

// Same purple-to-rose-gold gradient the sidebar's premium upgrade CTA uses,
// so every "unlock premium access" button reads as one brand family.
const CTA_GRADIENT = `linear-gradient(135deg, ${COLORS.amethyst} 0%, ${COLORS.roseGold} 165%)`;

type BillingCycle = "monthly" | "annual";

const Journey: React.FC = () => {
  const { user, updateCurrentUser } = useAuth();
  const navigate = useNavigate();
  const [selectedRegion, setSelectedRegion] = useState<WorldRegion | null>(
    null,
  );
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [openingRegion, setOpeningRegion] = useState<string | null>(null);

  // The app shell scrolls its own container (App.tsx's scrollRef, not the
  // window) and never resets scroll position on route change, so arriving
  // here mid-scroll from another page can leave the region grid starting
  // below the fold — reads as a blank page until the user scrolls down.
  // Force that container back to the top whenever this page mounts or the
  // grid/checkout view is swapped.
  useEffect(() => {
    document
      .querySelector(".h-full.overflow-y-auto")
      ?.scrollTo({ top: 0 });
  }, [selectedRegion]);

  const groupedRegions = useMemo(() => getRegionsByContinent(), []);
  const activeValue = user?.region || "healjai";

  // A region counts as unlocked when it's the account's active region OR it's
  // in the purchased-regions list a Journey checkout adds to (see
  // buildRegionUnlockUpdate in backend/controllers/paymentController.js).
  const { unlockedRegions, lockedRegions } = useMemo(() => {
    const unlockedValues = [activeValue, ...(user?.unlockedRegions || [])];
    const all = groupedRegions.flatMap((group) => group.regions);
    const unlocked: WorldRegion[] = [];
    const locked: WorldRegion[] = [];
    for (const region of all) {
      const owned = unlockedValues.some((v) => isSameRegion(region.value, v));
      (owned ? unlocked : locked).push(region);
    }
    return { unlockedRegions: unlocked, lockedRegions: locked };
  }, [groupedRegions, activeValue, user?.unlockedRegions]);

  const openCheckout = (region: WorldRegion) => {
    setCheckoutError("");
    setBillingCycle("monthly");
    setSelectedRegion(region);
  };

  const closeCheckout = () => {
    setSelectedRegion(null);
    setCheckoutError("");
    setIsRedirecting(false);
  };

  // Switches the account's active region to an already-unlocked one and drops
  // the user into its content — same call RegionSettings.tsx uses to change
  // region, just triggered from here instead of Settings.
  const openRegion = async (region: WorldRegion) => {
    if (isSameRegion(region.value, activeValue)) {
      navigate("/");
      return;
    }
    setOpeningRegion(region.value);
    try {
      const res = await api.put(`/backend/user/${user?._id}`, {
        region: region.value,
      });
      const updated = res.data?.updatedUser || {};
      updateCurrentUser({
        region: updated.region || region.value,
        unlockedRegions: updated.unlockedRegions || user?.unlockedRegions,
      });
      navigate("/");
    } catch {
      setOpeningRegion(null);
    }
  };

  const handlePay = async () => {
    if (!selectedRegion) return;
    setCheckoutError("");
    setIsRedirecting(true);
    try {
      // /payment/checkout only accepts a planId — price and currency are read
      // from the database so a tampered request body can't change what the
      // user is charged. So the region + cadence picked here first has to be
      // resolved to the matching SubscriptionPlans document (seeded per
      // region by backend/scripts/seedRegionPlans.js) before checkout.
      const cadenceText = billingCycle === "monthly" ? "per month" : "per year";
      const plansRes = await api.get("/backend/subscriptionPlans");
      const plans =
        plansRes?.data?.data ||
        plansRes?.data?.plans ||
        plansRes?.data ||
        [];
      const plan = (Array.isArray(plans) ? plans : []).find(
        (p: any) =>
          p?.region === selectedRegion.value &&
          String(p?.billingCadence || "").toLowerCase() === cadenceText,
      );

      if (!plan?._id) {
        setCheckoutError(
          `No ${billingCycle} plan is set up yet for ${selectedRegion.label}. Please try again later.`,
        );
        return;
      }

      const res = await api.post("/payment/checkout", { planId: plan._id });

      const url = res?.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      setCheckoutError("Stripe session URL missing. Please try again.");
    } catch (err: any) {
      setCheckoutError(
        err?.response?.data?.message ||
          err?.message ||
          "Checkout failed. Please try again.",
      );
    } finally {
      setIsRedirecting(false);
    }
  };

  if (selectedRegion) {
    return (
      <CheckoutPanel
        region={selectedRegion}
        billingCycle={billingCycle}
        onBillingCycleChange={setBillingCycle}
        onBack={closeCheckout}
        onPay={handlePay}
        isRedirecting={isRedirecting}
        error={checkoutError}
      />
    );
  }

  // Non-astria.fyi domains show only a simple welcome screen.
  if (!isAstriaDomain()) {
    return (
      <div className="flex-1 overflow-y-auto flex items-center justify-center">
        <p
          className="font-['Fraunces'] text-2xl font-semibold"
          style={{ color: "#2D1B44" }}
        >
          ยินดีต้อนรับสู่การเดินทาง 🌿
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[1100px] px-4 pb-28 pt-10">
        <div className="mx-auto max-w-[40rem] text-center">
          <div
            className="inline-flex items-center gap-2 uppercase"
            style={{
              fontSize: "0.68rem",
              fontWeight: 700,
              letterSpacing: "0.2em",
              color: COLORS.amethyst,
            }}
          >
            Your regions
          </div>
          <h1
            className="mt-[0.6rem] font-['Fraunces'] text-[2.1rem] font-bold tracking-[-0.01em]"
            style={{ color: COLORS.darkAmethyst }}
          >
            One companion, every culture
          </h1>
          <p
            className="mt-3 font-['Manrope'] text-[0.95rem] leading-[1.65]"
            style={{ color: "#4A4A4A" }}
          >
            {unlockedRegions.length} of{" "}
            {groupedRegions.reduce((n, g) => n + g.regions.length, 0)} regions
            unlocked. Unlock more below — each is priced in its own local
            currency.
          </p>
        </div>

        {unlockedRegions.length > 0 ? (
          <div className="mx-auto mt-7 w-full max-w-[900px]">
            <SectionHeader
              title="Unlocked"
              count={`${unlockedRegions.length} region${unlockedRegions.length === 1 ? "" : "s"}`}
            />
            <div className="grid grid-cols-1 gap-[0.875rem] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {unlockedRegions.map((region) => (
                <UnlockedCard
                  key={region.value}
                  region={region}
                  isActive={isSameRegion(region.value, activeValue)}
                  isOpening={openingRegion === region.value}
                  onOpen={() => openRegion(region)}
                />
              ))}
            </div>
          </div>
        ) : null}

        <div className="mx-auto mt-7 w-full max-w-[900px]">
          <SectionHeader
            title="Explore more regions"
            count={`${lockedRegions.length} locked`}
          />

          <div className="grid grid-cols-1 gap-[0.875rem] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {lockedRegions.map((region) => (
              <RegionCard
                key={region.value}
                region={region}
                onUnlock={() => openCheckout(region)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const SectionHeader: React.FC<{ title: string; count: string }> = ({
  title,
  count,
}) => (
  <div
    className="mb-4 flex items-baseline justify-between gap-4 border-b pb-[0.6rem]"
    style={{ borderColor: COLORS.hairline }}
  >
    <h2
      className="font-['Fraunces'] text-[1.15rem] font-bold"
      style={{ color: COLORS.darkAmethyst }}
    >
      {title}
    </h2>
    <span
      className="font-['Manrope'] text-[0.78rem] font-semibold"
      style={{ color: COLORS.muted }}
    >
      {count}
    </span>
  </div>
);

// Shared "card" look every region tile uses — 18px corners, a hairline
// border, and the lift-on-hover the rest of the site's tiles use (Worldwide
// chips, plan cards). Keeps this page from reading flatter than everywhere else.
const TILE_BASE =
  "group relative rounded-[18px] border bg-white p-4 text-left transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(45,27,68,0.1)]";

// RegionCard is a motion.button so it can use whileHover/whileTap — Framer
// Motion drives `transform` itself once it's in play, which would silently
// win over a CSS hover:-translate-y class, so the lift goes through Framer
// Motion's own whileHover instead of Tailwind's hover: variant.
const TILE_HOVER = { y: -4, boxShadow: "0 12px 28px rgba(45,27,68,0.1)" };
const TILE_TAP = { y: -2 };

const UnlockedCard: React.FC<{
  region: WorldRegion;
  isActive: boolean;
  isOpening: boolean;
  onOpen: () => void;
}> = ({ region, isActive, isOpening, onOpen }) => {
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={isOpening}
      className={`${TILE_BASE} disabled:cursor-wait disabled:opacity-70`}
      style={{
        border: `1.5px solid ${COLORS.amethyst}`,
        background:
          "linear-gradient(150deg, #FFFFFF 0%, #FDF7F3 55%, #F6EBDD 100%)",
        boxShadow: "0 10px 28px rgba(109,76,146,0.18)",
      }}
    >
      <span
        className="absolute right-2.5 top-2.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-['Manrope'] text-[9px] font-bold uppercase tracking-[0.12em] text-white"
        style={{
          backgroundColor: COLORS.amethyst,
          boxShadow: "0 3px 10px rgba(45,27,68,0.22)",
        }}
      >
        <Check className="h-2.5 w-2.5" />
        {isActive ? "Active now" : "Unlocked"}
      </span>

      <div className="mt-5 flex items-center gap-2.5">
        <span
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-xl"
          style={{ background: COLORS.cream }}
        >
          {region.flag}
        </span>
        <span className="min-w-0">
          <div className="truncate font-['Manrope'] text-sm font-bold" style={{ color: COLORS.darkAmethyst }}>
            {region.label}
          </div>
          <div className="truncate font-['Manrope'] text-[0.7rem] leading-[1.4]" style={{ color: COLORS.muted }}>
            {region.tagline}
          </div>
        </span>
      </div>

      <div className="mt-[0.7rem] flex items-center justify-between gap-2">
        <span className="font-['Manrope'] text-[0.78rem] font-bold" style={{ color: "#1F9D63" }}>
          Owned
        </span>
        <span
          className="whitespace-nowrap rounded-full px-2.5 py-1 font-['Manrope'] text-[0.68rem] font-bold"
          style={{ backgroundColor: "rgba(31,157,99,0.12)", color: "#1F9D63" }}
        >
          {isOpening ? "Opening…" : isActive ? "Open →" : "Switch to →"}
        </span>
      </div>
    </button>
  );
};

const RegionCard: React.FC<{
  region: WorldRegion;
  onUnlock: () => void;
}> = ({ region, onUnlock }) => {
  return (
    <motion.button
      type="button"
      onClick={onUnlock}
      whileHover={TILE_HOVER}
      whileTap={TILE_TAP}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="group relative rounded-[18px] border bg-white p-4 text-left"
      style={{ borderColor: COLORS.hairline }}
    >
      <div className="flex items-center gap-2.5">
        <span
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-xl"
          style={{ background: COLORS.cream }}
        >
          {region.flag}
        </span>
        <span className="min-w-0">
          <div className="truncate font-['Manrope'] text-sm font-bold" style={{ color: COLORS.darkAmethyst }}>
            {region.label}
          </div>
          <div className="truncate font-['Manrope'] text-[0.7rem] leading-[1.4]" style={{ color: COLORS.muted }}>
            {region.tagline}
          </div>
        </span>
      </div>

      <div className="mt-[0.7rem] flex items-center justify-between gap-2">
        <span className="font-['Manrope'] text-[0.78rem] font-bold" style={{ color: COLORS.darkAmethyst }}>
          {formatRegionPrice(region, region.monthlyPrice)}
          <small className="ml-0.5 font-medium" style={{ color: COLORS.muted }}>/mo</small>
        </span>
        <span
          className="whitespace-nowrap rounded-full px-2.5 py-1 font-['Manrope'] text-[0.68rem] font-bold text-white shadow-[0_3px_10px_rgba(45,27,68,0.22)] transition-transform group-hover:scale-105"
          style={{ background: CTA_GRADIENT }}
        >
          Unlock
        </span>
      </div>
    </motion.button>
  );
};

const STEPS = ["Choose plan", "Pay with Stripe", "Done"];

const CheckoutPanel: React.FC<{
  region: WorldRegion;
  billingCycle: BillingCycle;
  onBillingCycleChange: (cycle: BillingCycle) => void;
  onBack: () => void;
  onPay: () => void;
  isRedirecting: boolean;
  error: string;
}> = ({
  region,
  billingCycle,
  onBillingCycleChange,
  onBack,
  onPay,
  isRedirecting,
  error,
}) => {
  const monthlyEquivalent = region.annualPrice / 12;
  const annualSavingsPct = region.monthlyPrice
    ? Math.round(100 - (monthlyEquivalent / region.monthlyPrice) * 100)
    : 0;
  const price =
    billingCycle === "monthly" ? region.monthlyPrice : region.annualPrice;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-3xl px-4 pb-28 pt-8 md:px-8">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 font-['Manrope'] text-sm font-medium text-black/60 transition hover:text-black"
          >
            <ArrowLeft className="h-4 w-4" />
            Cancel
          </button>
          <div className="inline-flex items-center gap-1.5 font-['Manrope'] text-xs text-black/45">
            <Lock className="h-3.5 w-3.5" />
            Secure checkout · powered by Stripe
          </div>
        </div>

        <div className="mt-8 text-center">
          <div className="text-4xl">{region.flag}</div>
          <h1
            className="mt-3 font-['Fraunces'] text-3xl font-semibold"
            style={{ color: COLORS.ink }}
          >
            Unlock {region.label}
          </h1>
          <p className="mx-auto mt-2 max-w-md font-['Manrope'] text-sm text-black/60">
            Choose how you want access, then continue to Stripe's secure
            checkout. Cancel a subscription any time from your profile.
          </p>
        </div>

        <div className="mt-6 flex items-center justify-center gap-3 font-['Manrope'] text-xs text-black/50">
          {STEPS.map((step, i) => (
            <React.Fragment key={step}>
              {i > 0 ? <span className="h-px w-8 bg-black/15" /> : null}
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold"
                  style={{
                    backgroundColor: i === 0 ? COLORS.darkAmethyst : "#fff",
                    color: i === 0 ? "#fff" : COLORS.muted,
                    border: i === 0 ? "none" : `1px solid ${COLORS.hairline}`,
                  }}
                >
                  {i + 1}
                </span>
                <span className={i === 0 ? "font-semibold text-black/80" : ""}>
                  {step}
                </span>
              </span>
            </React.Fragment>
          ))}
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-[1fr_320px]">
          <div
            className="rounded-2xl bg-white p-5"
            style={{ border: `1px solid ${COLORS.hairline}` }}
          >
            <div className="font-['Manrope'] text-sm font-semibold text-[#1B1511]">
              Billing option
            </div>
            <p className="mt-1 font-['Manrope'] text-xs text-black/50">
              Prices shown in your region's local currency. Local taxes and
              payment methods are handled by Stripe at checkout.
            </p>

            <div className="mt-4 flex flex-col gap-3">
              <BillingOption
                active={billingCycle === "monthly"}
                title="Monthly"
                subtitle="Billed monthly, cancel anytime"
                price={`${formatRegionPrice(region, region.monthlyPrice)}/month`}
                onSelect={() => onBillingCycleChange("monthly")}
              />
              <BillingOption
                active={billingCycle === "annual"}
                title="Annual"
                badge={annualSavingsPct > 0 ? `SAVE ${annualSavingsPct}%` : undefined}
                subtitle="Billed once a year"
                price={`${formatRegionPrice(region, region.annualPrice)}/year`}
                onSelect={() => onBillingCycleChange("annual")}
              />
            </div>
          </div>

          <div
            className="rounded-2xl bg-white p-5"
            style={{ border: `1px solid ${COLORS.hairline}` }}
          >
            <div className="flex items-center gap-2 border-b pb-3" style={{ borderColor: COLORS.hairline }}>
              <span className="text-lg">{region.flag}</span>
              <div>
                <div className="font-['Manrope'] text-sm font-semibold text-[#1B1511]">
                  {region.label}
                </div>
                <div className="font-['Manrope'] text-xs text-black/45">
                  {region.tagline}
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-between font-['Manrope'] text-sm text-black/70">
              <span>{billingCycle === "monthly" ? "Monthly plan" : "Annual plan"}</span>
              <span className="font-semibold text-[#1B1511]">
                {formatRegionPrice(region, price)}
              </span>
            </div>

            <div className="mt-4 flex items-center justify-between border-t pt-4 font-['Manrope'] text-sm" style={{ borderColor: COLORS.hairline }}>
              <span className="font-semibold text-[#1B1511]">Total due today</span>
              <span className="text-lg font-bold text-[#1B1511]">
                {formatRegionPrice(region, price)}
              </span>
            </div>
            <p className="mt-1 font-['Manrope'] text-[11px] text-black/40">
              Charged in {region.currency}. Renews automatically until you
              cancel.
            </p>

            <button
              type="button"
              onClick={onPay}
              disabled={isRedirecting}
              className="mt-5 w-full rounded-full py-3 font-['Manrope'] text-sm font-semibold text-white shadow-[0_4px_14px_rgba(109,76,146,0.35)] transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
              style={{ background: CTA_GRADIENT }}
            >
              {isRedirecting ? "Redirecting to Stripe..." : "Continue to payment"}
            </button>
            <p className="mt-2 flex items-center justify-center gap-1.5 font-['Manrope'] text-[11px] text-black/40">
              <ShieldCheck className="h-3.5 w-3.5" />
              You'll be taken to Stripe to enter payment details on a secure
              page.
            </p>

            {error ? (
              <p className="mt-3 text-center font-['Manrope'] text-xs text-red-600">
                {error}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const BillingOption: React.FC<{
  active: boolean;
  title: string;
  subtitle: string;
  price: string;
  badge?: string;
  onSelect: () => void;
}> = ({ active, title, subtitle, price, badge, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className="flex items-center justify-between rounded-xl px-4 py-3.5 text-left transition-colors"
    style={{
      border: `1.5px solid ${active ? COLORS.darkAmethyst : COLORS.hairline}`,
      backgroundColor: active ? "#F6F1FF" : "#fff",
    }}
  >
    <div className="flex items-center gap-3">
      <span
        className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full"
        style={{ border: `1.5px solid ${active ? COLORS.darkAmethyst : "#C9C1B8"}` }}
      >
        {active ? (
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: COLORS.darkAmethyst }}
          />
        ) : null}
      </span>
      <div>
        <div className="flex items-center gap-2 font-['Manrope'] text-sm font-semibold text-[#1B1511]">
          {title}
          {badge ? (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
              {badge}
            </span>
          ) : null}
        </div>
        <div className="font-['Manrope'] text-xs text-black/45">{subtitle}</div>
      </div>
    </div>
    <div className="font-['Manrope'] text-sm font-semibold text-[#1B1511]">
      {price}
    </div>
  </button>
);

export default Journey;
